import { useEffect, useMemo, useRef, useState } from 'react';

import { useAppDispatch, useAppSelector } from '@verantos-dev/core-annotation-tool';

import { useGetSupervisorsQuery } from '../../api/createProjectApiSlice';
import {
  useAnnotationAgentAccountId,
  useIsAnnotationAgentOn,
} from '../../hooks/useAnnotationAgentAllocation';
import { addSelectedSupervisor, removeSelectedSupervisor } from '../../state/createProjectSlice';
import { selectSelectedSupervisors } from '../../state/selector';
import { SupervisorResponse } from '../../types/response';

interface UseSupervisorSelectionResults {
  supervisors: SupervisorResponse[];
  selectedSupervisors: SupervisorResponse[];
  handleAddSupervisor: (supervisor: SupervisorResponse) => void;
  handleRemoveSupervisor: (supervisor: SupervisorResponse) => void;
  supervisorInputValue: string;
  lastTypedSupervisorValue: React.MutableRefObject<string>;
  setSupervisorInputValue: (value: string) => void;
  isSupervisorsLoading: boolean;
}

export const useSupervisorSelection = (): UseSupervisorSelectionResults => {
  // TODO: cleanup with cp_annotation_createproject_agent ff
  // While the agent is on, agent accounts cannot be picked as supervisors.
  const isAgentOn = useIsAnnotationAgentOn();
  const agentId = useAnnotationAgentAccountId(isAgentOn);
  const {
    data: supervisorsData,
    isLoading: isSupervisorsLoading,
    isFetching: isSupervisorsFetching,
  } = useGetSupervisorsQuery({ includeNonAssignable: !isAgentOn });
  // While the list with agents is refetched without them, the previous data is still returned.
  const supervisors = useMemo(
    () => (supervisorsData?.data ?? []).filter((supervisor) => supervisor.id !== agentId),
    [supervisorsData, agentId]
  );

  const dispatch = useAppDispatch();
  const selectedSupervisors = useAppSelector(selectSelectedSupervisors);

  // TODO: cleanup with cp_annotation_createproject_agent ff
  // An agent picked by hand while the agent was off is dropped once it is turned on.
  useEffect(() => {
    if (agentId && selectedSupervisors.some((supervisor) => supervisor.id === agentId)) {
      dispatch(removeSelectedSupervisor(agentId));
    }
  }, [agentId, selectedSupervisors, dispatch]);

  const handleAddSupervisor = (supervisor: SupervisorResponse) => {
    dispatch(addSelectedSupervisor(supervisor));
  };
  const handleRemoveSupervisor = (supervisor: SupervisorResponse) => {
    dispatch(removeSelectedSupervisor(supervisor.id));
  };
  const [supervisorInputValue, setSupervisorInputValue] = useState<string>('');

  const lastTypedSupervisorValue = useRef<string>('');
  return {
    selectedSupervisors,
    handleRemoveSupervisor,
    supervisors,
    supervisorInputValue,
    lastTypedSupervisorValue,
    setSupervisorInputValue,
    handleAddSupervisor,
    isSupervisorsLoading: isSupervisorsLoading || isSupervisorsFetching,
  };
};
